/**
 * App-wide toast/snackbar mechanism.
 *
 * A small provider exposes `useToast()` so later specs can raise success/error
 * toasts after writes. Toasts render as MUI `Snackbar` + `Alert`, dark per the
 * design tokens (handoff "Design Tokens"), and auto-dismiss after ~4.2s.
 *
 * Adapted from the handoff reference (`mui_conversion/ToastProvider.tsx`) to our
 * repo conventions and strict TypeScript. No write screen consumes it in 0006 —
 * a minimal smoke usage + test is enough.
 */
import { Alert, Snackbar } from '@mui/material';
import { useCallback, useRef, useState, type ReactNode } from 'react';
import { SHADOWS } from '@/theme';
import { ToastContext, type ShowToast, type ToastSeverity } from './toastContext';

interface ToastItem {
  id: number;
  message: string;
  severity: ToastSeverity;
}
/** Auto-dismiss duration (ms) — matches the handoff's ~4.2s. */
const AUTO_HIDE_MS = 4200;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const idRef = useRef(0);

  const show = useCallback<ShowToast>((message, severity = 'success') => {
    const id = (idRef.current += 1);
    setItems((xs) => [...xs, { id, message, severity }]);
  }, []);

  const dismiss = useCallback((id: number) => {
    setItems((xs) => xs.filter((x) => x.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={show}>
      {children}
      {items.map((t, i) => (
        <Snackbar
          key={t.id}
          open
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          autoHideDuration={AUTO_HIDE_MS}
          onClose={(_event, reason) => {
            // Ignore click-away so stacked toasts don't dismiss each other.
            if (reason === 'clickaway') return;
            dismiss(t.id);
          }}
          // Stack multiple toasts vertically.
          sx={{ mb: `${i * 60}px` }}
        >
          <Alert
            onClose={() => dismiss(t.id)}
            severity={t.severity}
            variant="filled"
            sx={{
              bgcolor: '#2D2A26',
              color: '#fff',
              boxShadow: SHADOWS.snackbar,
              '& .MuiAlert-icon': {
                color: t.severity === 'error' ? '#E8A' : '#8FCBA6',
              },
            }}
          >
            {t.message}
          </Alert>
        </Snackbar>
      ))}
    </ToastContext.Provider>
  );
}
